import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api.js';
import { Table, type Column } from '../../components/ui/Table.js';
import { Dialog } from '../../components/ui/Dialog.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { StatusPill, type PillVariant } from '../../components/ui/StatusPill.js';
import { useToast } from '../../components/ui/Toast.js';
import {
  ProviderNotConfiguredCard,
  errMessage,
  isProviderNotConfigured,
  parseIdCsv,
  retryUnlessClientError,
} from './shared.js';
import './infrastructure.css';

interface ForwardingRule {
  entry_protocol: string;
  entry_port: number;
  target_protocol: string;
  target_port: number;
  certificate_id?: string;
  tls_passthrough?: boolean;
}

interface HealthCheck {
  protocol: string;
  port: number;
  path?: string;
  check_interval_seconds?: number;
  response_timeout_seconds?: number;
  healthy_threshold?: number;
  unhealthy_threshold?: number;
}

interface LoadBalancer {
  id: string;
  name: string;
  ip: string | null;
  status: string;
  regionSlug: string | null;
  dropletIds: number[];
  forwardingRules: ForwardingRule[];
  healthCheck: HealthCheck | null;
  tags: string[];
  createdAt: string | null;
}

interface DoRegion {
  slug: string;
  name: string;
  available: boolean;
  sizes: string[];
}

const ENTRY_PROTOCOLS = ['http', 'https', 'http2', 'http3', 'tcp', 'udp'] as const;
const TARGET_PROTOCOLS = ['http', 'https', 'http2', 'tcp', 'udp'] as const;
const HC_PROTOCOLS = ['http', 'https', 'tcp'] as const;

interface RuleForm {
  entry_protocol: string;
  entry_port: string;
  target_protocol: string;
  target_port: string;
}

const DEFAULT_RULE: RuleForm = {
  entry_protocol: 'http',
  entry_port: '80',
  target_protocol: 'http',
  target_port: '80',
};

function parsePort(value: string): number | null {
  const n = Number(value.trim());
  if (!Number.isInteger(n) || n < 1 || n > 65535) return null;
  return n;
}

function statusVariant(status: string): PillVariant {
  if (status === 'active') return 'ok';
  if (status === 'new') return 'warn';
  if (status === 'errored') return 'err';
  return 'neutral';
}

export function LoadBalancersPage() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const lbsQuery = useQuery<LoadBalancer[]>({
    queryKey: ['do', 'load-balancers'],
    queryFn: () => apiFetch<LoadBalancer[]>('/do/load-balancers'),
    retry: retryUnlessClientError,
  });

  // ── create/edit dialog ──
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<LoadBalancer | null>(null);
  const [name, setName] = useState('');
  const [region, setRegion] = useState('');
  const [rules, setRules] = useState<RuleForm[]>([{ ...DEFAULT_RULE }]);
  const [hcEnabled, setHcEnabled] = useState(false);
  const [hcProtocol, setHcProtocol] = useState('http');
  const [hcPort, setHcPort] = useState('80');
  const [hcPath, setHcPath] = useState('/');
  const [dropletIdsText, setDropletIdsText] = useState('');

  const regionsQuery = useQuery<DoRegion[]>({
    queryKey: ['do', 'regions'],
    queryFn: () => apiFetch<DoRegion[]>('/do/regions'),
    enabled: dialogOpen && editing === null,
    retry: retryUnlessClientError,
  });

  const openCreate = () => {
    setEditing(null);
    setName('');
    setRegion('');
    setRules([{ ...DEFAULT_RULE }]);
    setHcEnabled(false);
    setHcProtocol('http');
    setHcPort('80');
    setHcPath('/');
    setDropletIdsText('');
    setDialogOpen(true);
  };

  const openEdit = (lb: LoadBalancer) => {
    setEditing(lb);
    setName(lb.name);
    setRegion(lb.regionSlug ?? '');
    setRules(
      lb.forwardingRules.length > 0
        ? lb.forwardingRules.map((r) => ({
            entry_protocol: r.entry_protocol,
            entry_port: String(r.entry_port),
            target_protocol: r.target_protocol,
            target_port: String(r.target_port),
          }))
        : [{ ...DEFAULT_RULE }],
    );
    setHcEnabled(lb.healthCheck !== null);
    setHcProtocol(lb.healthCheck?.protocol ?? 'http');
    setHcPort(String(lb.healthCheck?.port ?? 80));
    setHcPath(lb.healthCheck?.path ?? '/');
    setDropletIdsText(lb.dropletIds.join(', '));
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  const updateRule = (index: number, patch: Partial<RuleForm>) => {
    setRules((cur) => cur.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  interface LbBody {
    name: string;
    region?: string;
    forwarding_rules: ForwardingRule[];
    health_check?: HealthCheck;
    droplet_ids?: number[];
    tags?: string[];
  }

  const buildBody = (): LbBody | string => {
    const trimmedName = name.trim();
    if (!trimmedName) return 'name is required';
    const forwarding_rules: ForwardingRule[] = [];
    for (const rule of rules) {
      const entryPort = parsePort(rule.entry_port);
      const targetPort = parsePort(rule.target_port);
      if (entryPort === null || targetPort === null) {
        return 'forwarding rule ports must be numbers between 1 and 65535';
      }
      forwarding_rules.push({
        entry_protocol: rule.entry_protocol,
        entry_port: entryPort,
        target_protocol: rule.target_protocol,
        target_port: targetPort,
      });
    }
    if (forwarding_rules.length === 0) return 'at least one forwarding rule is required';

    const dropletIds = parseIdCsv(dropletIdsText);
    if (dropletIds === null) return 'droplet ids must be comma-separated numeric ids';

    let health_check: HealthCheck | undefined;
    if (hcEnabled) {
      const port = parsePort(hcPort);
      if (port === null) return 'health check port must be a number between 1 and 65535';
      health_check = {
        protocol: hcProtocol,
        port,
        ...(hcProtocol !== 'tcp' && hcPath.trim() ? { path: hcPath.trim() } : {}),
      };
    }

    return {
      name: trimmedName,
      ...(region ? { region } : {}),
      forwarding_rules,
      ...(health_check ? { health_check } : {}),
      ...(dropletIds.length > 0 ? { droplet_ids: dropletIds } : {}),
      // preserve existing tags on full-replace updates
      ...(editing && editing.tags.length > 0 ? { tags: editing.tags } : {}),
    };
  };

  const saveMut = useMutation({
    mutationFn: ({ id, body }: { id: string | null; body: LbBody }) =>
      id === null
        ? apiFetch<LoadBalancer>('/do/load-balancers', { method: 'POST', body })
        : apiFetch<LoadBalancer>(`/do/load-balancers/${id}`, { method: 'PUT', body }),
    onSuccess: async (saved, vars) => {
      await queryClient.invalidateQueries({ queryKey: ['do', 'load-balancers'] });
      closeDialog();
      toast.success(
        vars.id === null
          ? `Load balancer "${saved.name}" is being created`
          : `Load balancer "${saved.name}" updated`,
      );
    },
    onError: (err) => toast.error(errMessage(err, 'failed to save load balancer')),
  });

  const submit = () => {
    const body = buildBody();
    if (typeof body === 'string') {
      toast.error(body);
      return;
    }
    saveMut.mutate({ id: editing?.id ?? null, body });
  };

  // ── delete ──
  const [deleteTarget, setDeleteTarget] = useState<LoadBalancer | null>(null);
  const deleteMut = useMutation({
    mutationFn: (lb: LoadBalancer) =>
      apiFetch<{ ok: boolean }>(`/do/load-balancers/${lb.id}`, {
        method: 'DELETE',
        body: { confirmName: lb.name },
      }),
    onSuccess: async (_res, lb) => {
      await queryClient.invalidateQueries({ queryKey: ['do', 'load-balancers'] });
      toast.success(`Load balancer "${lb.name}" deleted`);
      setDeleteTarget(null);
    },
    onError: (err) => toast.error(errMessage(err, 'delete failed')),
  });

  const columns: Column<LoadBalancer>[] = [
    { key: 'name', header: 'Name', render: (lb) => <span className="infra-name">{lb.name}</span> },
    {
      key: 'status',
      header: 'Status',
      render: (lb) => <StatusPill variant={statusVariant(lb.status)}>{lb.status}</StatusPill>,
    },
    {
      key: 'ip',
      header: 'IP',
      render: (lb) => <span className="mono">{lb.ip ?? '—'}</span>,
    },
    {
      key: 'droplets',
      header: 'Droplets',
      render: (lb) => <span className="ui-cell-mute">{lb.dropletIds.length}</span>,
    },
    {
      key: 'region',
      header: 'Region',
      render: (lb) => <span className="ui-cell-mute">{lb.regionSlug ?? '—'}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (lb) => (
        <div className="ui-cell-actions">
          <button className="btn btn-ghost btn-sm" onClick={() => openEdit(lb)}>
            Edit
          </button>
          <button className="btn btn-ghost-danger btn-sm" onClick={() => setDeleteTarget(lb)}>
            Delete
          </button>
        </div>
      ),
    },
  ];

  if (isProviderNotConfigured(lbsQuery.error)) {
    return (
      <div>
        <div className="page-head">
          <div>
            <h1>Load Balancers</h1>
            <p className="page-sub">DigitalOcean load balancers and forwarding rules.</p>
          </div>
        </div>
        <ProviderNotConfiguredCard provider="DigitalOcean" message={lbsQuery.error.message} />
      </div>
    );
  }

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Load Balancers</h1>
          <p className="page-sub">DigitalOcean load balancers and forwarding rules.</p>
        </div>
        <button className="btn btn-amber" onClick={openCreate}>
          Create load balancer
        </button>
      </div>

      <Table
        columns={columns}
        rows={lbsQuery.data ?? []}
        rowKey={(lb) => lb.id}
        empty={
          lbsQuery.isLoading
            ? 'Loading…'
            : lbsQuery.isError
              ? errMessage(lbsQuery.error, 'failed to load load balancers')
              : 'No load balancers yet. Create one to get started.'
        }
      />

      {/* create / edit dialog */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        title={editing ? `Edit "${editing.name}"` : 'Create load balancer'}
        width={620}
      >
        <form
          className="dialog-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!saveMut.isPending) submit();
          }}
        >
          <div className="field-grid-2">
            <div className="field">
              <label htmlFor="lb-name">Name</label>
              <input
                id="lb-name"
                autoFocus
                maxLength={255}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="web-lb"
              />
            </div>
            <div className="field">
              <label htmlFor="lb-region">Region</label>
              {editing ? (
                <input id="lb-region" className="mono" value={region || '—'} disabled />
              ) : (
                <select id="lb-region" value={region} onChange={(e) => setRegion(e.target.value)}>
                  <option value="" disabled>
                    {regionsQuery.isLoading ? 'Loading…' : 'Select region'}
                  </option>
                  {(regionsQuery.data ?? [])
                    .filter((r) => r.available)
                    .map((r) => (
                      <option key={r.slug} value={r.slug}>
                        {r.name} ({r.slug})
                      </option>
                    ))}
                </select>
              )}
            </div>
          </div>

          <div className="rule-section">
            <div className="rule-section-head">
              <span>Forwarding rules</span>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setRules((cur) => [...cur, { ...DEFAULT_RULE }])}
              >
                Add rule
              </button>
            </div>
            {rules.map((rule, i) => (
              <div key={i} className="rule-row">
                <select
                  className="rule-proto"
                  aria-label="Entry protocol"
                  value={rule.entry_protocol}
                  onChange={(e) => updateRule(i, { entry_protocol: e.target.value })}
                >
                  {ENTRY_PROTOCOLS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
                <input
                  className="rule-port mono"
                  aria-label="Entry port"
                  value={rule.entry_port}
                  onChange={(e) => updateRule(i, { entry_port: e.target.value })}
                  placeholder="80"
                />
                <span className="rule-arrow">→</span>
                <select
                  className="rule-proto"
                  aria-label="Target protocol"
                  value={rule.target_protocol}
                  onChange={(e) => updateRule(i, { target_protocol: e.target.value })}
                >
                  {TARGET_PROTOCOLS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
                <input
                  className="rule-port mono"
                  aria-label="Target port"
                  value={rule.target_port}
                  onChange={(e) => updateRule(i, { target_port: e.target.value })}
                  placeholder="80"
                />
                <button
                  type="button"
                  className="rule-remove"
                  aria-label="Remove rule"
                  disabled={rules.length === 1}
                  onClick={() => setRules((cur) => cur.filter((_, idx) => idx !== i))}
                >
                  ×
                </button>
              </div>
            ))}
          </div>

          <div className="rule-section">
            <div className="rule-section-head">
              <span>Health check</span>
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={hcEnabled}
                  onChange={(e) => setHcEnabled(e.target.checked)}
                />
                Configure
              </label>
            </div>
            {hcEnabled ? (
              <div className="rule-row">
                <select
                  className="rule-proto"
                  aria-label="Health check protocol"
                  value={hcProtocol}
                  onChange={(e) => setHcProtocol(e.target.value)}
                >
                  {HC_PROTOCOLS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
                <input
                  className="rule-port mono"
                  aria-label="Health check port"
                  value={hcPort}
                  onChange={(e) => setHcPort(e.target.value)}
                  placeholder="80"
                />
                <input
                  className="rule-wide mono"
                  aria-label="Health check path"
                  value={hcPath}
                  onChange={(e) => setHcPath(e.target.value)}
                  placeholder="/"
                  disabled={hcProtocol === 'tcp'}
                />
              </div>
            ) : (
              <p className="rule-empty">DigitalOcean applies its default health check.</p>
            )}
          </div>

          <div className="field">
            <label htmlFor="lb-droplets">Droplet IDs (comma separated)</label>
            <textarea
              id="lb-droplets"
              className="mono"
              rows={2}
              value={dropletIdsText}
              onChange={(e) => setDropletIdsText(e.target.value)}
              placeholder="12345678, 23456789"
            />
          </div>

          <div className="dialog-actions">
            <button type="button" className="btn btn-ghost" onClick={closeDialog}>
              Cancel
            </button>
            <button type="submit" className="btn btn-amber" disabled={saveMut.isPending}>
              {saveMut.isPending
                ? 'Saving…'
                : editing
                  ? 'Save changes'
                  : 'Create load balancer'}
            </button>
          </div>
        </form>
      </Dialog>

      {/* delete confirm */}
      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget)}
        title="Delete load balancer"
        targetName={deleteTarget?.name ?? ''}
        description="Traffic stops being forwarded immediately. Attached droplets are not affected."
        confirmLabel="Delete load balancer"
        busy={deleteMut.isPending}
      />
    </div>
  );
}
